import React, { useState, useEffect } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faCheckCircle,
    faCalendarDay,
    faDoorOpen,
    faSpinnerThird,
    faDownload,
    faBriefcase,
    faRunning,
    faGavel,
    faQuestionCircle,
    faPersonWalkingDashedLineArrowRight,
    faSearch,
} from "@fortawesome/pro-solid-svg-icons";

export default function MonthendDashboard({ folderType = "renewals", initialPropertyId, onOpenBookingCancellations }) {
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [cancellationStats, setCancellationStats] = useState(null);
    const [bookings, setBookings] = useState([]);
    const [availableMonths, setAvailableMonths] = useState([]);
    const [availableChoices, setAvailableChoices] = useState([]);
    const [properties, setProperties] = useState([]);
    
    // Filters
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [selectedChoice, setSelectedChoice] = useState("");
    const [selectedProperty, setSelectedProperty] = useState(initialPropertyId || "");
    const [lateNoticeOnly, setLateNoticeOnly] = useState(false);
    
    // Selected stat for showing bookings
    const [selectedStat, setSelectedStat] = useState(null);
    const [loadingBookings, setLoadingBookings] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    
    const [exporting, setExporting] = useState(false);

    const fetchDashboard = async () => {
        setLoading(true);
        try {
            const params = { folder_type: folderType };
            if (startDate) params.start_month = startDate.substring(0, 7); // YYYY-MM
            if (endDate) params.end_month = endDate.substring(0, 7);
            if (selectedChoice) params.choice_keyword = selectedChoice;
            if (selectedProperty) params.property_id = selectedProperty;
            if (lateNoticeOnly) params.late_notice_only = 1;

            const response = await axios.get(`${process.env.REACT_APP_API_URL}monthend/dashboard`, { params });
            setStats(response.data.stats);
            setCancellationStats(response.data.cancellation_stats);
            setAvailableMonths(response.data.availableMonths || []);
            setAvailableChoices(response.data.availableChoices || []);
            if (response.data.properties) setProperties(response.data.properties);
            
            // Re-fetch bookings for the currently selected stat card
            if (selectedStat) {
                fetchBookingsForStat(selectedStat);
            }
        } catch (error) {
            console.error("Failed to fetch dashboard:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchBookingsForStat = async (statType) => {
        setLoadingBookings(true);
        try {
            const params = { folder_type: folderType };
            if (startDate) params.start_month = startDate.substring(0, 7);
            if (endDate) params.end_month = endDate.substring(0, 7);
            if (selectedProperty) params.property_id = selectedProperty;
            if (selectedChoice) params.choice_keyword = selectedChoice;
            if (lateNoticeOnly) params.late_notice_only = 1;
            
            // Only set stat-specific filters if no choice filter is active
            if (!selectedChoice) {
                switch(statType) {
                    case 'total':
                        params.show_all = 1;
                        break;
                    case 'yes_renew':
                        params.choice_keyword = 'YES, RENEW';
                        break;
                    case 'm2m':
                        params.choice_keyword = 'MONTH 2 MONTH';
                        break;
                    case 'leaving':
                        params.choice_keyword = 'LEAVING';
                        break;
                    case 'early_termination':
                    case 'abscond':
                    case 'eviction':
                    case 'unspecified':
                        params.choice_keyword = 'LEAVING';
                        params.cancellation_type = statType;
                        break;
                }
            } else {
                // Still need show_all for total when choice is set
                if (statType === 'total') {
                    params.show_all = 1;
                }
                if (['early_termination', 'abscond', 'eviction', 'unspecified'].includes(statType)) {
                    params.cancellation_type = statType;
                }
            }

            const response = await axios.get(`${process.env.REACT_APP_API_URL}monthend/dashboard`, { params });
            setBookings(response.data.bookings || []);
        } catch (error) {
            console.error("Failed to fetch bookings:", error);
        } finally {
            setLoadingBookings(false);
        }
    };

    const handleStatClick = (statType) => {
        if (selectedStat === statType) {
            // Clicking same stat again closes it
            setSelectedStat(null);
            setBookings([]);
        } else {
            setSelectedStat(statType);
            fetchBookingsForStat(statType);
        }
    };

    useEffect(() => {
        fetchDashboard();
    }, [folderType, selectedProperty]);

    const handleExport = async () => {
        setExporting(true);
        try {
            // Export only the currently displayed bookings
            if (bookings.length === 0) {
                alert("No bookings to export");
                setExporting(false);
                return;
            }

            // Build params for backend export
            const params = { 
                folder_type: folderType,
                format: 'xlsx'
            };
            if (startDate) params.start_month = startDate.substring(0, 7);
            if (endDate) params.end_month = endDate.substring(0, 7);
            if (selectedProperty) params.property_id = selectedProperty;
            
            // Add stat-specific filters
            if (['early_termination', 'abscond', 'eviction', 'unspecified'].includes(selectedStat)) {
                params.choice_keyword = 'LEAVING';
                params.cancellation_type = selectedStat;
            } else if (selectedStat === 'yes_renew') {
                params.choice_keyword = 'YES, RENEW';
            } else if (selectedStat === 'm2m') {
                params.choice_keyword = 'MONTH 2 MONTH';
            } else if (selectedStat === 'leaving') {
                params.choice_keyword = 'LEAVING';
            }

            const response = await axios.get(`${process.env.REACT_APP_API_URL}monthend/export`, { 
                params,
                responseType: 'blob'
            });
            
            // Create download link
            const blob = new Blob([response.data], { 
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
            });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const filename = `monthend_${selectedStat}_${new Date().toISOString().split('T')[0]}.xlsx`;
            a.download = filename;
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Export failed:", error);
            alert("Export failed");
        } finally {
            setExporting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <FontAwesomeIcon icon={faSpinnerThird} className="text-4xl text-indigo-600 animate-spin" />
            </div>
        );
    }

    return (
        <div className="p-6 space-y-6">

            {/* Filters - Above Stats */}
            <div className="bg-white rounded-lg shadow p-4">
                <h3 className="text-sm font-medium text-gray-900 mb-3">Filters</h3>
                <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                    {/* Property Filter */}
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Property</label>
                        <select
                            value={selectedProperty}
                            onChange={(e) => setSelectedProperty(e.target.value)}
                            className="block w-full rounded-md border-2 border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm h-[38px]"
                        >
                            <option value="">All Properties</option>
                            {properties.map((prop) => (
                                <option key={prop.id} value={prop.id}>{prop.name}</option>
                            ))}
                        </select>
                    </div>

                    {/* Start Date */}
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Start Date</label>
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="block w-full rounded-md border-2 border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm h-[38px]"
                        />
                    </div>

                    {/* End Date */}
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">End Date</label>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="block w-full rounded-md border-2 border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm h-[38px]"
                        />
                    </div>

                    {/* Filter by Choice */}
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Filter by Choice</label>
                        <select
                            value={selectedChoice}
                            onChange={(e) => setSelectedChoice(e.target.value)}
                            className="block w-full rounded-md border-2 border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm h-[38px]"
                        >
                            <option value="">All Choices</option>
                            {availableChoices.map((choice) => (
                                <option key={choice} value={choice}>{choice}</option>
                            ))}
                        </select>
                    </div>

                    {/* Late Notice Only */}
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Late Notice Only</label>
                        <div className="flex items-center h-[38px]">
                            <input
                                type="checkbox"
                                checked={lateNoticeOnly}
                                onChange={(e) => setLateNoticeOnly(e.target.checked)}
                                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                            />
                            <span className="ml-2 text-sm text-gray-700">Only show late notice</span>
                        </div>
                    </div>

                    {/* Filter Button */}
                    <div className="flex items-end">
                        <button
                            onClick={() => fetchDashboard()}
                            className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                            Filter
                        </button>
                    </div>
                </div>
            </div>

            {/* Main Stats Cards - Clickable */}
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                {/* Total */}
                <button
                    onClick={() => handleStatClick('total')}
                    className={`bg-white overflow-hidden shadow rounded-lg text-left transition-all hover:shadow-lg hover:scale-105 ${
                        selectedStat === 'total' ? 'ring-2 ring-indigo-500' : ''
                    }`}
                >
                    <div className="p-5">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <div className="rounded-md bg-indigo-500 p-3">
                                    <FontAwesomeIcon icon={faCheckCircle} className="h-6 w-6 text-white" />
                                </div>
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 truncate">Total Responses</dt>
                                    <dd className="text-3xl font-semibold text-gray-900">{stats?.total || 0}</dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                </button>

                {/* Yes, Renew */}
                <button
                    onClick={() => handleStatClick('yes_renew')}
                    className={`bg-white overflow-hidden shadow rounded-lg text-left transition-all hover:shadow-lg hover:scale-105 ${
                        selectedStat === 'yes_renew' ? 'ring-2 ring-green-500' : ''
                    }`}
                >
                    <div className="p-5">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <div className="rounded-md bg-green-500 p-3">
                                    <FontAwesomeIcon icon={faCheckCircle} className="h-6 w-6 text-white" />
                                </div>
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 truncate">Yes, Renew</dt>
                                    <dd className="text-3xl font-semibold text-gray-900">{stats?.yes_renew || 0}</dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                </button>

                {/* Month to Month */}
                <button
                    onClick={() => handleStatClick('m2m')}
                    className={`bg-white overflow-hidden shadow rounded-lg text-left transition-all hover:shadow-lg hover:scale-105 ${
                        selectedStat === 'm2m' ? 'ring-2 ring-blue-500' : ''
                    }`}
                >
                    <div className="p-5">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <div className="rounded-md bg-blue-500 p-3">
                                    <FontAwesomeIcon icon={faCalendarDay} className="h-6 w-6 text-white" />
                                </div>
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 truncate">Month 2 Month</dt>
                                    <dd className="text-3xl font-semibold text-gray-900">{stats?.month_to_month || 0}</dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                </button>

                {/* Leaving */}
                <button
                    onClick={() => handleStatClick('leaving')}
                    className={`bg-white overflow-hidden shadow rounded-lg text-left transition-all hover:shadow-lg hover:scale-105 ${
                        selectedStat === 'leaving' ? 'ring-2 ring-red-500' : ''
                    }`}
                >
                    <div className="p-5">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <div className="rounded-md bg-red-500 p-3">
                                    <FontAwesomeIcon icon={faDoorOpen} className="h-6 w-6 text-white" />
                                </div>
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 truncate">Leaving</dt>
                                    <dd className="text-3xl font-semibold text-gray-900">{stats?.leaving || 0}</dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                </button>
            </div>

            {/* Cancellation Type Stats - Clickable */}
            {cancellationStats && (
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                    {/* Early Termination */}
                    <button
                        onClick={() => handleStatClick('early_termination')}
                        className={`bg-white overflow-hidden shadow rounded-lg text-left transition-all hover:shadow-lg hover:scale-105 ${
                            selectedStat === 'early_termination' ? 'ring-2 ring-blue-500' : ''
                        }`}
                    >
                        <div className="p-5">
                            <div className="flex items-center">
                                <div className="flex-shrink-0">
                                    <div className="rounded-md bg-blue-100 p-3">
                                        <FontAwesomeIcon icon={faBriefcase} className="h-6 w-6 text-blue-600" />
                                    </div>
                                </div>
                                <div className="ml-5 w-0 flex-1">
                                    <dl>
                                        <dt className="text-sm font-medium text-gray-500 truncate">Early Termination</dt>
                                        <dd className="text-3xl font-semibold text-gray-900">{cancellationStats.early_termination || 0}</dd>
                                    </dl>
                                </div>
                            </div>
                        </div>
                    </button>

                    {/* Abscond */}
                    <button
                        onClick={() => handleStatClick('abscond')}
                        className={`bg-white overflow-hidden shadow rounded-lg text-left transition-all hover:shadow-lg hover:scale-105 ${
                            selectedStat === 'abscond' ? 'ring-2 ring-yellow-500' : ''
                        }`}
                    >
                        <div className="p-5">
                            <div className="flex items-center">
                                <div className="flex-shrink-0">
                                    <div className="rounded-md bg-yellow-100 p-3">
                                        <FontAwesomeIcon icon={faRunning} className="h-6 w-6 text-yellow-600" />
                                    </div>
                                </div>
                                <div className="ml-5 w-0 flex-1">
                                    <dl>
                                        <dt className="text-sm font-medium text-gray-500 truncate">Abscond</dt>
                                        <dd className="text-3xl font-semibold text-gray-900">{cancellationStats.abscond || 0}</dd>
                                    </dl>
                                </div>
                            </div>
                        </div>
                    </button>

                    {/* Eviction */}
                    <button
                        onClick={() => handleStatClick('eviction')}
                        className={`bg-white overflow-hidden shadow rounded-lg text-left transition-all hover:shadow-lg hover:scale-105 ${
                            selectedStat === 'eviction' ? 'ring-2 ring-red-500' : ''
                        }`}
                    >
                        <div className="p-5">
                            <div className="flex items-center">
                                <div className="flex-shrink-0">
                                    <div className="rounded-md bg-red-100 p-3">
                                        <FontAwesomeIcon icon={faGavel} className="h-6 w-6 text-red-600" />
                                    </div>
                                </div>
                                <div className="ml-5 w-0 flex-1">
                                    <dl>
                                        <dt className="text-sm font-medium text-gray-500 truncate">Eviction</dt>
                                        <dd className="text-3xl font-semibold text-gray-900">{cancellationStats.eviction || 0}</dd>
                                    </dl>
                                </div>
                            </div>
                        </div>
                    </button>

                    {/* Unspecified */}
                    <button
                        onClick={() => handleStatClick('unspecified')}
                        className={`bg-white overflow-hidden shadow rounded-lg text-left transition-all hover:shadow-lg hover:scale-105 ${
                            selectedStat === 'unspecified' ? 'ring-2 ring-gray-500' : ''
                        }`}
                    >
                        <div className="p-5">
                            <div className="flex items-center">
                                <div className="flex-shrink-0">
                                    <div className="rounded-md bg-gray-100 p-3">
                                        <FontAwesomeIcon icon={faQuestionCircle} className="h-6 w-6 text-gray-600" />
                                    </div>
                                </div>
                                <div className="ml-5 w-0 flex-1">
                                    <dl>
                                        <dt className="text-sm font-medium text-gray-500 truncate">Unspecified</dt>
                                        <dd className="text-3xl font-semibold text-gray-900">{cancellationStats.unspecified || 0}</dd>
                                    </dl>
                                </div>
                            </div>
                        </div>
                    </button>
                </div>
            )}

            {/* Bookings List - Only shown when stat is clicked */}
            {selectedStat && (
                <div className="bg-white rounded-lg shadow">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <div className="flex justify-between items-center gap-4">
                            <h3 className="text-lg font-medium text-gray-900 flex-shrink-0">
                                Bookings for {selectedStat.replace('_', ' ').toUpperCase()} ({bookings.filter(booking => {
                                    if (!searchTerm) return true;
                                    const search = searchTerm.toLowerCase();
                                    return (
                                        booking.booking_reference?.toLowerCase().includes(search) ||
                                        booking.user?.name?.toLowerCase().includes(search) ||
                                        booking.user?.surname?.toLowerCase().includes(search) ||
                                        booking.property?.name?.toLowerCase().includes(search) ||
                                        booking.unit?.name?.toLowerCase().includes(search)
                                    );
                                }).length})
                            </h3>
                            <div className="relative" style={{ width: '300px' }}>
                                <input
                                    type="text"
                                    placeholder="Search"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="block w-full rounded-md border-2 border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm pl-10 h-[38px]"
                                />
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <FontAwesomeIcon icon={faSearch} className="h-4 w-4 text-gray-400" />
                                </div>
                            </div>
                            {bookings.length > 0 && (
                                <button
                                    onClick={handleExport}
                                    disabled={exporting}
                                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 flex-shrink-0 h-[38px]"
                                >
                                    <FontAwesomeIcon icon={exporting ? faSpinnerThird : faDownload} className={`mr-2 ${exporting ? 'animate-spin' : ''}`} />
                                    {exporting ? "Exporting..." : "Export to Excel"}
                                </button>
                            )}
                        </div>
                    </div>
                    <div className="p-6">
                        {loadingBookings ? (
                            <div className="flex justify-center py-8">
                                <FontAwesomeIcon icon={faSpinnerThird} className="text-2xl text-indigo-600 animate-spin" />
                            </div>
                        ) : bookings.length === 0 ? (
                            <p className="text-center text-gray-500 py-8">No bookings found</p>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Booking
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                MDA Code
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Property
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Location
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Choice
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                End Month
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Cancelled
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Late Notice
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Source
                                            </th>
                                            {onOpenBookingCancellations && (
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                </th>
                                            )}
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {bookings.filter(booking => {
                                            if (!searchTerm) return true;
                                            const search = searchTerm.toLowerCase();
                                            return (
                                                booking.booking_reference?.toLowerCase().includes(search) ||
                                                booking.user?.name?.toLowerCase().includes(search) ||
                                                booking.user?.surname?.toLowerCase().includes(search) ||
                                                booking.property?.name?.toLowerCase().includes(search) ||
                                                booking.unit?.name?.toLowerCase().includes(search) ||
                                                booking.user_mda?.mda_tenant_id?.toString().toLowerCase().includes(search)
                                            );
                                        }).map((booking) => {
                                            // Combine location and tag
                                            let locationDisplay = booking.location || 'N/A';
                                            if (booking.tag && booking.tag !== 0) {
                                                locationDisplay += ` (Tag: ${booking.tag})`;
                                            }
                                            
                                            return (
                                                <tr key={booking.id} className="hover:bg-gray-50">
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                        <a
                                                            href={`/booking/${(booking.location || 'all').toLowerCase().replace(/\s+/g, '')}?user_id=${booking.user?.id}&booking_id=${booking.id}&property_id=${booking.property_id || ''}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="font-medium text-indigo-600 hover:text-indigo-900 hover:underline"
                                                        >
                                                            {booking.booking_reference || 'N/A'}
                                                        </a>
                                                        <div className="text-xs text-gray-500">{[booking.user?.name, booking.user?.surname].filter(Boolean).join(' ') || ''}</div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                        {booking.user_mda?.mda_tenant_id || 'N/A'}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                        <div>{booking.property?.name || 'N/A'}</div>
                                                        {booking.unit?.name && (
                                                            <div className="text-xs text-gray-400">{booking.unit.name}</div>
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                        {locationDisplay}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm">
                                                        <div className="font-medium text-gray-900">{booking.renewal_choice || 'N/A'}</div>
                                                        {booking.renewal_choice_date && (
                                                            <div className="text-xs text-gray-500">{new Date(booking.renewal_choice_date).toLocaleDateString()}</div>
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                        {booking.cancellation_end_month || '-'}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm">
                                                        {booking.cancelled ? (
                                                            <>
                                                                <div className="font-medium text-gray-900">
                                                                    {booking.cancellation_type ? booking.cancellation_type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Unspecified'}
                                                                </div>
                                                                {booking.cancelled_at && (
                                                                    <div className="text-xs text-gray-500">{new Date(booking.cancelled_at).toLocaleDateString()}</div>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <span className="text-gray-400">-</span>
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                        {booking.late_notice ? (
                                                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                                                                Yes
                                                            </span>
                                                        ) : (
                                                            <span className="text-gray-400">-</span>
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm">
                                                        {booking.latest_renewal_history ? (
                                                            <>
                                                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                                                    booking.latest_renewal_history.source === 'whatsapp' ? 'bg-green-100 text-green-800' :
                                                                    booking.latest_renewal_history.source === 'manual' ? 'bg-blue-100 text-blue-800' :
                                                                    'bg-gray-100 text-gray-800'
                                                                }`}>
                                                                    {booking.latest_renewal_history.source || 'system'}
                                                                </span>
                                                                <div className="text-xs text-gray-500 mt-1">
                                                                    {booking.latest_renewal_history.actioned_by && typeof booking.latest_renewal_history.actioned_by === 'object'
                                                                        ? `${booking.latest_renewal_history.actioned_by.name || ''} ${booking.latest_renewal_history.actioned_by.surname || ''}`.trim()
                                                                        : (booking.latest_renewal_history.source === 'whatsapp' ? 'Client' : '')
                                                                    }
                                                                </div>
                                                            </>
                                                        ) : '-'}
                                                    </td>
                                                    {onOpenBookingCancellations && (
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                            <button
                                                                type="button"
                                                                onClick={() => onOpenBookingCancellations(booking)}
                                                                className="text-indigo-400 hover:text-indigo-600 transition-colors"
                                                                title="Open Cancellations & Renewals"
                                                            >
                                                                <FontAwesomeIcon icon={faPersonWalkingDashedLineArrowRight} className="text-sm" />
                                                            </button>
                                                        </td>
                                                    )}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
